import { TRANSLATION_KEYS as QUALITY_KEYS } from 'partner360/components/qualityIndicator/configs/translations'
import type {
  IFinancialPlanQuality,
  IFinancialPlanQualityPillar,
  IFinancialPlanQualityTableRow,
} from 'partner360/features/financialPlanQuality'
import { TRANSLATION_KEYS } from 'partner360/features/financialPlanQuality/configs/translations'
import { getColumns } from 'partner360/features/financialPlanQuality/ui/financialPlanQuality/components/financialPlanQualityPillar/helpers/getColumns'

import type { ICockpitExportCell, ICockpitExportPillar, ICockpitExportTable } from '../../../../api'
import { getPillarScore, toExportText } from '../../../components/cockpitExport'

type TTranslate = (key: string) => string

/** The details table's mark for a value it does not have — `getColumns`' own. */
const NO_VALUE = '-'

type TFinSobQualityPillarKey = 'financialGrowth' | 'sobGrowth'

/** Which pillar has its details table open. */
export type TFinSobQualityOpenDetails = Record<TFinSobQualityPillarKey, boolean>

/** The pillars the tile shows, in order: Financial growth always, SOB growth only when it has
 *  categories to score — as `FinancialPlanQualityContent` shows them on the partner's page. */
export const getFinSobQualityPillarKeys = (quality: IFinancialPlanQuality) =>
  (['financialGrowth', 'sobGrowth'] as const).filter(
    (key): key is TFinSobQualityPillarKey =>
      key === 'financialGrowth' || quality.sobGrowth.categories.length > 0
  )

/**
 * A pillar's details table as it opens: its top-level categories, each with a caret where it has
 * subcategories. The library table keeps the user's own expanding, sorting and paging to itself,
 * so the deck repeats what the table first shows.
 *
 * Headers, Planned YoY and Market target are read off the very columns that draw the table.
 */
const getDetailsTable = (
  pillar: IFinancialPlanQualityPillar,
  translate: TTranslate
): ICockpitExportTable => {
  const columns = getColumns(pillar.valueUnit, () => undefined)
  const titleOf = (field: string) =>
    translate(columns.find((column) => column.field === field)?.title ?? '')
  const textOf = (field: string, row: IFinancialPlanQualityTableRow) => {
    const column = columns.find((item) => item.field === field)
    const shown = column?.renderFunction?.(row, column)

    return typeof shown === 'string' ? shown : ''
  }

  return {
    columns: [
      { key: 'category', title: titleOf('category') },
      { key: 'planned', title: titleOf('planned'), isNumeric: true },
      { key: 'target', title: titleOf('target'), isNumeric: true },
      { key: 'points', title: titleOf('points'), isNumeric: true },
      /** Drawn as a Tag, so it sits centred like a score. */
      { key: 'bandScore', title: titleOf('band'), isNumeric: true },
    ],
    rows: pillar.categories.map((category) => {
      const hasChildren = Boolean(category.children?.length)
      const row: IFinancialPlanQualityTableRow = { ...category, parentId: '', hasChildren }
      const band: ICockpitExportCell = category.band
        ? { text: translate(QUALITY_KEYS.tag[category.band]), badge: category.band }
        : { text: NO_VALUE }

      return {
        id: category.id,
        cells: {
          category: {
            text: translate(category.category),
            depth: 0,
            hasChildren,
            isExpanded: false,
          },
          planned: { text: textOf('planned', row), value: category.planned },
          target: { text: textOf('target', row), value: category.target },
          points: {
            text: category.points === null ? NO_VALUE : String(category.points),
            value: category.points,
          },
          bandScore: band,
        },
      }
    }),
  }
}

interface IParams {
  quality: IFinancialPlanQuality
  openDetails: TFinSobQualityOpenDetails
  translate: TTranslate
}

/** A single partner's Financial & SOB Plan Quality, as `CockpitFinSobQualityPillars` draws it. */
export const getFinSobQualityPillarsExport = ({
  quality,
  openDetails,
  translate,
}: IParams): ICockpitExportPillar[] =>
  getFinSobQualityPillarKeys(quality).map((key) => {
    const pillar = quality[key]

    return {
      key,
      title: translate(TRANSLATION_KEYS.quality[key].title),
      ...getPillarScore(pillar.score, pillar.band, translate),
      description: toExportText(translate(TRANSLATION_KEYS.quality[key].description)),
      table: openDetails[key] ? getDetailsTable(pillar, translate) : undefined,
    }
  })
